import { useEffect, useState } from 'react';
import {
  BarChart3, Package, Users, Receipt, Mail, UserPlus, CalendarClock, Shield, LifeBuoy, Database, UserCog, Briefcase, Bot,
} from 'lucide-react';
import PageShell from '../components/PageShell';
import PortalNavMenu from '../components/portal/PortalNavMenu';
import { type AppTab } from '../components/portal/AppTabs';
import SchedulerPanel from '../components/SchedulerPanel';
import OverviewApp from './portal/OverviewApp';
import ProductsApp from './portal/ProductsApp';
import CustomersApp from './portal/CustomersApp';
import BillingApp from './portal/BillingApp';
import NewsletterApp from './portal/NewsletterApp';
import SignupApp from './portal/SignupApp';
import RolesApp from './portal/RolesApp';
import TicketsApp from './portal/TicketsApp';
import UsageApp from './portal/UsageApp';
import UsersApp from './portal/UsersApp';
import JobSitesApp from './portal/JobSitesApp';
import UserAgentsAdmin from './portal/UserAgentsAdmin';
import { useAuth } from '../hooks/useAuth';
import { billingApi, type StripeConfigResponse } from '../api/billing';

type PortalTab = 'overview' | 'products' | 'customers' | 'users' | 'billing' | 'newsletter' | 'signup' | 'roles' | 'tickets' | 'usage' | 'scheduler' | 'jobsites' | 'agents';

const TABS: AppTab[] = [
  { key: 'overview', label: 'Overview', icon: BarChart3, desc: 'Business snapshot and key metrics.' },
  { key: 'products', label: 'Products', icon: Package, desc: 'What you sell and how it is priced.' },
  { key: 'customers', label: 'Customers', icon: Users, desc: 'Customer records and accounts.' },
  { key: 'users', label: 'Users', icon: UserCog, desc: 'People who can sign in to the platform.' },
  { key: 'agents', label: 'User Agents', icon: Bot, desc: "Each user's agents and their focus prompts." },
  { key: 'billing', label: 'Billing', icon: Receipt, desc: 'Payments, invoices, and Stripe.' },
  { key: 'newsletter', label: 'Newsletter', icon: Mail, desc: 'Promotional email campaigns.' },
  { key: 'signup', label: 'Signup', icon: UserPlus, desc: 'New-account signup flow and settings.' },
  { key: 'roles', label: 'Roles & Rollout', icon: Shield, desc: 'Roles and which features reach whom.' },
  { key: 'usage', label: 'Usage', icon: Database, desc: 'Storage and resource consumption.' },
  { key: 'tickets', label: 'Support Tickets', icon: LifeBuoy, desc: 'Customer support conversations.' },
  { key: 'scheduler', label: 'Scheduler', icon: CalendarClock, desc: 'Background jobs and their schedules.' },
  { key: 'jobsites', label: 'Job Sites', icon: Briefcase, desc: 'The job boards the platform scans.' },
];

/**
 * Customer Portal — the owner's commerce control module.
 *
 * Each tab is a full application (owner directive 2026-09-24), not a card:
 * Overview (statistical analytics), Products, Customers, Billing, Newsletter,
 * Signup, and Scheduler. The tab bar is icon-only so it never wraps; labels
 * reveal on press-and-hold.
 */
export default function CustomerPortalPage({ initialTab = 'overview' }: { initialTab?: PortalTab }) {
  const [tab, setTab] = useState<PortalTab>(initialTab);
  const [stripeConfig, setStripeConfig] = useState<StripeConfigResponse | null>(null);
  const { user } = useAuth();
  const isOwner = Boolean(user?.is_owner);
  // Job Sites and User Agents are admin-only features.
  const ownerOnlyTabs = new Set(['jobsites', 'agents']);
  const tabs = TABS.filter(t => !ownerOnlyTabs.has(t.key) || isOwner);

  const reloadStripe = () => {
    billingApi.getStripeConfig().then(setStripeConfig).catch(() => setStripeConfig(null));
  };
  useEffect(() => { setTab(initialTab); }, [initialTab]);

  useEffect(() => { reloadStripe(); /* eslint-disable-next-line */ }, []);

  const stripeReady = Boolean(stripeConfig?.configured && stripeConfig?.is_active);

  return (
    <PageShell
      title="Customer Portal"
      purpose="Products, customers, billing, payments, newsletters, and signup."
      actions={
        <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${stripeReady ? 'bg-green-500/10 text-green-600 dark:text-green-400' : 'bg-amber-500/10 text-amber-600 dark:text-amber-400'}`}>
          {stripeReady ? 'Stripe Active' : 'Stripe Not Configured'}
        </span>
      }
    >
      {/* Owner directive 2026-09-30: one pop-out menu (icon + readable label)
          replaces the cramped icon-only tab bar. */}
      <PortalNavMenu tabs={tabs} active={tab} onChange={k => setTab(k as PortalTab)} idPrefix="portal" />

      {tab === 'overview' && <OverviewApp />}
      {tab === 'products' && <ProductsApp />}
      {tab === 'customers' && <CustomersApp />}
      {tab === 'users' && <UsersApp />}
      {tab === 'billing' && <BillingApp stripeConfig={stripeConfig} onStripeSaved={reloadStripe} />}
      {tab === 'newsletter' && <NewsletterApp />}
      {tab === 'signup' && <SignupApp />}
      {tab === 'roles' && <RolesApp />}
      {tab === 'usage' && <UsageApp />}
      {tab === 'tickets' && <TicketsApp />}
      {tab === 'scheduler' && <SchedulerPanel />}
      {tab === 'jobsites' && isOwner && <JobSitesApp />}
      {tab === 'agents' && isOwner && <UserAgentsAdmin embedded />}
    </PageShell>
  );
}
